import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Shield, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

export const InsuranceSection = () => {
  const { user, api } = useAuth();
  const [plans, setPlans] = useState([]);
  const [userInsurance, setUserInsurance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, userRes] = await Promise.all([
        api.get('/insurance/plans'),
        api.get('/insurance')
      ]);
      setPlans(plansRes.data);
      setUserInsurance(userRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (plan) => {
    if (user.kyc_status !== 'approved') {
      toast.error('KYC approval required for insurance');
      return;
    }
    try {
      await api.post('/insurance/apply', {
        plan_type: plan.id,
        coverage_amount: plan.coverage
      });
      toast.success('Insurance application submitted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Application failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="insurance-section">
      <h1 className="font-syne text-2xl font-bold text-gray-900">Insurance</h1>

      {/* KYC Warning */}
      {user?.kyc_status !== 'approved' && (
        <Card className="card-raoul p-4 border-yellow-300 bg-yellow-50">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="text-gray-900 font-medium">KYC Verification Required</p>
              <p className="text-gray-500 text-sm">Complete your KYC verification to apply for insurance.</p>
            </div>
          </div>
        </Card>
      )}

      {/* Insurance Plans */}
      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const hasApplied = userInsurance.some(ins => ins.plan_type === plan.id);
          return (
            <Card key={plan.id} className="card-raoul p-6 relative overflow-hidden">
              {plan.id === 'premium' && (
                <div className="absolute top-4 right-4 bg-afriperx-green text-white text-xs px-2 py-1 rounded-full">
                  Popular
                </div>
              )}
              <Shield className={`w-10 h-10 mb-4 ${plan.id === 'premium' ? 'text-afriperx-gold' : 'text-gray-400'}`} />
              <h3 className="font-syne text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
              <p className="text-3xl font-bold text-gray-900 mb-1">
                R{plan.monthly_premium}
                <span className="text-sm text-gray-500 font-normal">/month</span>
              </p>
              <p className="text-gray-500 text-sm mb-4">Coverage: R{plan.coverage.toLocaleString()}</p>
              
              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-500" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleApply(plan)}
                disabled={hasApplied || user?.kyc_status !== 'approved'}
                className={`w-full ${plan.id === 'premium' ? 'btn-primary' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
                data-testid={`insurance-apply-${plan.id}`}
              >
                {hasApplied ? 'Applied' : 'Apply Now'}
              </Button>
            </Card>
          );
        })}
      </div>

      {/* User's Insurance */}
      {userInsurance.length > 0 && (
        <Card className="card-raoul p-6">
          <h2 className="font-syne text-lg font-bold text-gray-900 mb-4">Your Insurance Applications</h2>
          <div className="space-y-3">
            {userInsurance.map((ins) => (
              <div key={ins.id} className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-gray-900 font-medium capitalize">{ins.plan_type} Cover</p>
                  <p className="text-gray-500 text-sm">Coverage: R{ins.coverage_amount?.toLocaleString()}</p>
                </div>
                <span className={`status-badge ${ins.status === 'approved' ? 'status-approved' : 'status-pending'}`}>
                  {ins.status}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
